import { Button, Input, Select, Typography } from '@smartgift/frontend-toolkit';
import { useFormik } from 'formik';
import { cloneDeep } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useDebounce } from 'use-debounce';

import {
  MAX_ADDRESS_LENGTH,
  MAX_COMPANY_NAME_LENGTH,
  MAX_ZIP_CODE_LENGTH,
  US_STATE_LIST
} from '@hero/constants';
import { externalHooks } from '@hero/services';
import { useUserSignUp } from '@hero/services/organization-service/hooks';

import * as S from './style';
import { infoSchema } from './validationSchema';

function SignUpInfo({ activeStep, setActiveStep, setSignUpData, signUpData }) {
  const { t } = useTranslation();
  const { mutateAsync: signUpUser, isLoading } = useUserSignUp();
  const [isValidZipCode, setIsValidZipCode] = useState(true);

  const formik = useFormik({
    initialValues: {
      companyName: signUpData?.companyName ?? '',
      companyAddress: signUpData?.companyAddress ?? '',
      zipCode: signUpData?.zipCode ?? '',
      city: signUpData?.city ?? '',
      state: signUpData?.state ?? '',
      street: signUpData?.street ?? ''
    },
    validateOnMount: true,
    validationSchema: infoSchema,
    onSubmit: async data => {
      const signUpClone = cloneDeep(signUpData);
      const signUpBody = {
        ...signUpClone,
        companyName: data.companyName,
        companyAddress: data.companyAddress,
        zipCode: data.zipCode,
        city: data.city,
        state: data.state,
        meta: {
          subscribedToNewsletter: true
        }
      };
      await signUpUser({
        registrationToken: signUpBody.registrationToken,
        password: signUpBody.password,
        companyName: signUpBody.companyName,
        lastName: signUpBody.lastName,
        firstName: signUpBody.firstName,
        meta: signUpBody.meta,
        address: {
          street: signUpBody.companyAddress,
          city: signUpBody.city,
          state: signUpBody.state.value,
          country: 'US',
          addressType: 'Business',
          postcode: signUpBody.zipCode.toString(),
          telephone: signUpBody.phone
        }
      });
      setSignUpData(signUpBody);
      setActiveStep(activeStep + 1);
    }
  });

  const renderSelect = useCallback(
    () => (
      <Select
        placeholder={t('state') + '*'}
        onChange={state => {
          formik.setValues({
            ...formik.values,
            state: state
          });
        }}
        value={formik.values.state}
        options={US_STATE_LIST}
      />
    ),
    [formik.values.state]
  );

  const [debounceZipCode] = useDebounce(formik?.values?.zipCode?.toString(), 500);

  externalHooks.useGetZipCode({
    zipCode: debounceZipCode,
    options: {
      onSuccess({ state, city }) {
        setIsValidZipCode(true);
        formik.setValues({
          ...formik.values,
          state: US_STATE_LIST.find(usState => usState.value === state.short_name) || state,
          city
        });
      },
      onError() {
        setIsValidZipCode(false);
        formik.setValues({
          ...formik.values,
          state: '',
          city: ''
        });
      }
    }
  });

  return (
    <S.SignUpInfo>
      <Typography variant="h4">{t('companyInformation')}</Typography>
      <Input
        label={`${t('companyName')}*`}
        name={'companyName'}
        onChange={formik.handleChange}
        value={formik.values.companyName}
        variant="outlined"
        block
        inputProps={{ maxLength: MAX_COMPANY_NAME_LENGTH }}
      />
      <Input
        label={`${t('companyAddress')}*`}
        name={'companyAddress'}
        onChange={formik.handleChange}
        value={formik.values.companyAddress}
        variant="outlined"
        block
        inputProps={{ maxLength: MAX_ADDRESS_LENGTH }}
      />
      <Input
        label={`${t('zipCode')}*`}
        name={'zipCode'}
        onChange={formik.handleChange}
        value={formik.values.zipCode}
        error={!isValidZipCode}
        variant="outlined"
        block
        inputProps={{ maxLength: MAX_ZIP_CODE_LENGTH }}
      />

      {!isValidZipCode && formik.values.zipCode && (
        <Typography color="error" variant="caption">
          {t('invalidZipCode')}
        </Typography>
      )}

      <S.SignUpInfoGroup>
        <Input
          label={`${t('city')}*`}
          name={'city'}
          onChange={formik.handleChange}
          value={formik.values.city}
          variant="outlined"
          block
        />

        {renderSelect()}
      </S.SignUpInfoGroup>

      <S.SignUpInfoTerms variant="body2" color="fg" emphasis="default">
        <Trans
          i18nKey="signUpTermsInfo"
          components={{
            a1: (
              <S.SignUpInfoTermsItem target="_blank" href={t('termsOfUseUrl')} rel="noreferrer" />
            ),
            a2: (
              <S.SignUpInfoTermsItem
                target="_blank"
                href={t('privacyPolicyUrl')}
                rel="noreferrer"
              />
            )
          }}
        />
      </S.SignUpInfoTerms>

      <S.SignUpInfoGroup>
        <Button
          color="primary"
          onClick={() => {
            setActiveStep(activeStep - 1);
          }}
          disabled={isLoading}
          variant="outlined"
          block
        >
          {t('back')}
        </Button>
        <Button
          color="primary"
          disabled={!formik.isValid || isLoading}
          loading={isLoading}
          onClick={formik.handleSubmit}
          block
        >
          {t('submit')}
        </Button>
      </S.SignUpInfoGroup>
    </S.SignUpInfo>
  );
}

SignUpInfo.propTypes = {
  activeStep: PropTypes.number,
  setActiveStep: PropTypes.func,
  setSignUpData: PropTypes.func,
  signUpData: PropTypes.object,
  setStepId: PropTypes.func
};

export { SignUpInfo };
